import { motion } from 'framer-motion'
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { AppPhoneTrio } from '@/components/AppPhoneTrio'
import { MeshBackdrop } from '@/components/MeshBackdrop'
import {
  BookDemoScheduler,
  type CalendlyAudience,
} from '@/components/BookDemoScheduler'
import { Footer } from '@/components/Footer'
import { Header } from '@/components/Header'
import {
  formatSlotLabel,
  getUserTimezone,
  type CalendlyBookingResult,
} from '@/lib/calendlyBooking'
import { applyPageMeta } from '@/lib/seo'
import { trackBookDemoPageView } from '@/lib/openaiAds'

function parseAudience(value: string | null): CalendlyAudience {
  if (value === 'community' || value === 'academy' || value === 'venue') {
    return value
  }
  return 'community'
}

const DEMO_POINTS = [
  {
    title: 'First, we hear about your setup',
    body: 'Your sport, your courts, how many games you run a week, and what you want to improve.',
  },
  {
    title: 'Then a live tour of the app',
    body: 'We open Statbox on a real game so you see the stats, highlights, and feed your players would get.',
  },
  {
    title: 'And we map out your start',
    body: 'What the setup looks like at your venue, and what it costs.',
  },
]

export default function BookDemo() {
  const [searchParams] = useSearchParams()
  const audience = parseAudience(searchParams.get('audience'))

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [booking, setBooking] = useState<CalendlyBookingResult | null>(null)
  const timezone = getUserTimezone()

  useEffect(() => {
    applyPageMeta({
      title: 'Book a Demo — Statbox',
      description:
        'Book a free 30-minute Statbox demo. Pick a time that works for you.',
      path: '/book-demo',
    })
    trackBookDemoPageView()
  }, [])

  return (
    <div className="relative min-h-dvh bg-black text-white overflow-x-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <MeshBackdrop dimmed />
      </div>

      <Header />

      <main className="relative z-10 pt-24 sm:pt-28 pb-16 lg:pb-20">
        <div className="mx-auto w-full max-w-[1240px] px-4 sm:px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors mb-6 sm:mb-8"
          >
            <ArrowLeft size={15} aria-hidden />
            Back to home
          </Link>

          {booking ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto max-w-lg rounded-[28px] border border-white/10 bg-[#16141c]/92 backdrop-blur-xl p-8 sm:p-12 text-center shadow-[0_24px_80px_rgba(0,0,0,0.45)] overflow-hidden relative"
            >
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(113,205,217,0.16),transparent_55%),radial-gradient(ellipse_at_bottom,rgba(251,191,136,0.1),transparent_50%)]"
              />
              <div className="relative">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-400/15 border border-emerald-300/30 mb-6 shadow-[0_0_32px_rgba(52,211,153,0.25)]">
                  <CheckCircle2 size={30} className="text-emerald-300" aria-hidden />
                </div>
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-3 text-balance">
                  You&apos;re in — see you soon
                </h1>
                <p className="text-base sm:text-lg text-white/85 mb-3">
                  {formatSlotLabel(booking.start_time, timezone)}
                </p>
                <p className="text-[15px] text-white/55 leading-relaxed max-w-sm mx-auto">
                  {email
                    ? `We'll send a calendar invite to ${email}. Looking forward to meeting you.`
                    : "We'll send a calendar invite shortly. Looking forward to meeting you."}
                </p>
                <Link
                  to="/"
                  className="mt-10 inline-flex min-h-[52px] items-center rounded-2xl px-8 py-3.5 text-[15px] font-semibold text-[#0a0f14] bg-[#71cdd9] hover:bg-[#9de4ed] shadow-lg shadow-[#71cdd9]/25 transition-colors"
                >
                  Back to Statbox
                </Link>
              </div>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 2xl:grid-cols-[minmax(300px,420px)_minmax(720px,1fr)] gap-10 2xl:gap-14 items-start">
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="min-w-0"
              >
                <p className="inline-flex items-center rounded-full border border-[#71cdd9]/25 bg-[#71cdd9]/10 px-3 py-1 text-[12px] font-medium text-[#9de4ed] mb-4">
                  30 minutes · free · just a chat
                </p>
                <h1 className="text-[2rem] leading-[1.12] sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-white text-balance">
                  We&apos;ll show you around. You pick a time.
                </h1>
                <p className="mt-4 mb-2 text-[15px] sm:text-base text-white/65 leading-relaxed max-w-md relative z-[1]">
                  This is what players open after the game. Ratings, clips,
                  and physicals, right on their phone. We&apos;ll walk through
                  it together.
                </p>

                <div className="mt-7 sm:mt-9 grid grid-cols-1 lg:grid-cols-[minmax(250px,330px)_minmax(0,1fr)] 2xl:grid-cols-1 gap-8 lg:gap-10 2xl:gap-9 items-center">
                  <AppPhoneTrio className="lg:order-2 2xl:order-none" />

                  <div className="hidden sm:block max-w-md lg:order-1 2xl:order-none">
                    <p className="text-[12px] font-medium text-white/40 mb-1">
                      What the 30 minutes covers
                    </p>
                    <ul className="divide-y divide-white/[0.07]">
                      {DEMO_POINTS.map(({ title, body }) => (
                        <li key={title} className="py-4">
                          <p className="text-[14.5px] font-semibold text-white leading-snug">
                            {title}
                          </p>
                          <p className="mt-1 text-[13.5px] leading-relaxed text-white/45">
                            {body}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.05 }}
                className="min-w-0"
              >
                <div className="rounded-[28px] border border-white/[0.08] bg-[#16141c]/90 backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.5)] relative z-0 overflow-hidden">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(113,205,217,0.1),transparent_50%),radial-gradient(ellipse_at_bottom_left,rgba(251,191,136,0.07),transparent_45%)]"
                  />
                  <div className="relative px-6 pt-6 sm:px-8 sm:pt-8 lg:px-10 lg:pt-10">
                    <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white">
                      When are you free?
                    </h2>
                    <p className="mt-1.5 text-[14px] sm:text-[15px] text-white/55 leading-relaxed">
                      Grab a slot that feels easy. We&apos;ll send the invite
                      to your inbox.
                    </p>
                  </div>
                  <div className="relative px-6 pb-6 sm:px-8 sm:pb-8 lg:px-10 lg:pb-10 pt-6">
                    <BookDemoScheduler
                      audience={audience}
                      layout="stacked"
                      name={name}
                      email={email}
                      onNameChange={setName}
                      onEmailChange={setEmail}
                      onBooked={setBooking}
                      confirmLabel="Book this time"
                      footerNote="No pitch. Just a walkthrough of the app."
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </main>

      <div className="relative z-10 hidden md:block">
        <Footer />
      </div>
    </div>
  )
}
